import { CharacterDataService } from '../../services/CharacterDataService';
import { buildSmartPasteCharacterImport } from '../../utils/SmartPasteCharacterImport';
import { hydrateCharacterCombatState } from '../../utils/CharacterCombatReadiness';
import {
  ABILITY_ORDER,
  ALIGNMENT_OPTIONS,
  BACKGROUND_KEYS,
  BACKGROUND_OPTIONS,
  NAME_BANKS,
  PERSONALITY_HOOKS,
  SKILL_ABILITY_MAP,
  SPECIES_OPTIONS_BY_RULESET,
  STANDARD_ARRAY,
  STARTER_CLASS_KEYS,
  STARTER_CLASS_OPTIONS,
  STARTER_GENERATOR_VERSION
} from './generatorData';

const DEFAULT_RULESET = '2024';
const DEFAULT_TONE = 'optimized';
const DEFAULT_LEVEL = 1;
const SHIELD_BONUS = 2;

const DAMAGE_DICE_PATTERN = /(\d+d\d+)/i;
const RANGE_PROPERTY_PATTERN = /\((\d+)\s*\/\s*(\d+)\)/;

const stableHash = (value) => {
  const input = String(value ?? '');
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const createRng = (seed) => {
  let state = stableHash(seed || 'starter-generator') || 1;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let next = Math.imul(state ^ (state >>> 15), 1 | state);
    next ^= next + Math.imul(next ^ (next >>> 7), 61 | next);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
};

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const clampLevel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_LEVEL;
  return Math.min(20, Math.max(1, Math.floor(numeric)));
};

const toSentenceCase = (value) => String(value || '')
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const toArticle = (value) => (/^[aeiou]/i.test(String(value || '')) ? 'an' : 'a');

const abilityModifier = (score) => Math.floor((Number(score || 10) - 10) / 2);

const proficiencyBonusForLevel = (level) => Math.ceil(level / 4) + 1;

const unique = (values) => {
  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const normalized = typeof value === 'string' ? value.toLowerCase() : value;
    if (normalized === '' || normalized === null || normalized === undefined || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(value);
  });

  return result;
};

const pickOne = (rng, values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const index = Math.floor(rng() * values.length);
  return values[index] ?? values[0];
};

const shuffle = (rng, values) => {
  const copy = [...values];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(rng() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
};

const normalizeFeatureEntry = (entry, fallbackDescription = '') => {
  const name = String(entry?.name || '').trim();
  if (!name) return null;

  const description = String(
    entry?.description
    || entry?.text
    || entry?.desc
    || fallbackDescription
  ).trim();

  return {
    ...deepClone(entry),
    name,
    description,
    text: description || entry?.text || ''
  };
};

const resolveRuleset = (requested) => (
  Object.prototype.hasOwnProperty.call(SPECIES_OPTIONS_BY_RULESET, requested)
    ? requested
    : DEFAULT_RULESET
);

const resolveClassKey = (requested, rng) => (
  STARTER_CLASS_OPTIONS[requested]
    ? requested
    : pickOne(rng, STARTER_CLASS_KEYS)
);

const resolveBackgroundKey = (requested, rng) => (
  BACKGROUND_OPTIONS[requested]
    ? requested
    : pickOne(rng, BACKGROUND_KEYS)
);

const resolveSpeciesKey = (requested, ruleset, rng) => {
  const pool = SPECIES_OPTIONS_BY_RULESET[ruleset] || SPECIES_OPTIONS_BY_RULESET[DEFAULT_RULESET];
  if (requested && pool?.[requested]) {
    return requested;
  }

  return pickOne(rng, Object.keys(pool || {}));
};

const resolveAbilityPriority = (classOption, background, tone) => {
  if (tone === 'background-forward') {
    return unique([
      ...(background?.abilityChoices || []),
      ...(classOption?.primaryAbilities || []),
      ...(classOption?.secondaryAbilities || []),
      ...ABILITY_ORDER
    ]);
  }

  if (tone === 'balanced') {
    return unique([
      classOption?.primaryAbilities?.[0],
      ...(background?.abilityChoices || []),
      ...(classOption?.secondaryAbilities || []),
      ...(classOption?.primaryAbilities?.slice(1) || []),
      ...ABILITY_ORDER
    ]);
  }

  return unique([
    ...(classOption?.primaryAbilities || []),
    ...(background?.abilityChoices || []),
    ...(classOption?.secondaryAbilities || []),
    ...ABILITY_ORDER
  ]);
};

const assignAbilityScores = (classOption, background, species, ruleset, tone) => {
  const priority = resolveAbilityPriority(classOption, background, tone);
  const scores = {};

  priority.forEach((ability, index) => {
    scores[ability] = STANDARD_ARRAY[index] ?? STANDARD_ARRAY[STANDARD_ARRAY.length - 1];
  });

  ABILITY_ORDER.forEach((ability) => {
    if (!Number.isFinite(scores[ability])) {
      scores[ability] = STANDARD_ARRAY[STANDARD_ARRAY.length - 1];
    }
  });

  if (ruleset === '2024') {
    const backgroundPriority = unique([
      ...priority.filter((ability) => (background?.abilityChoices || []).includes(ability)),
      ...(background?.abilityChoices || []),
      ...ABILITY_ORDER
    ]);
    const firstBonus = backgroundPriority[0];
    const secondBonus = backgroundPriority.find((ability) => ability !== firstBonus) || priority.find((ability) => ability !== firstBonus);

    if (firstBonus) scores[firstBonus] += 2;
    if (secondBonus) scores[secondBonus] += 1;
  }

  Object.entries(species?.abilityBonuses || {}).forEach(([ability, bonus]) => {
    if (!Object.prototype.hasOwnProperty.call(scores, ability)) return;
    scores[ability] += Number(bonus || 0);
  });

  return scores;
};

const buildName = (speciesKey, rng) => {
  const bank = NAME_BANKS[speciesKey] || NAME_BANKS.human;
  const first = pickOne(rng, bank.first) || 'Adventurer';
  const last = pickOne(rng, bank.last) || 'Vale';
  return `${first} ${last}`;
};

const normalizeEquipmentShape = (equipment) => ({
  weapons: Array.isArray(equipment?.weapons) ? deepClone(equipment.weapons) : [],
  armor: Array.isArray(equipment?.armor) ? deepClone(equipment.armor) : [],
  items: unique([
    ...(Array.isArray(equipment?.items) ? deepClone(equipment.items) : []),
    ...(Array.isArray(equipment?.other) ? deepClone(equipment.other) : [])
  ]),
  valuables: Array.isArray(equipment?.valuables) ? deepClone(equipment.valuables) : [],
  currency: equipment?.currency && typeof equipment.currency === 'object'
    ? { ...equipment.currency }
    : {}
});

const inferWeaponAbility = (weapon, abilities) => {
  const name = String(weapon?.name || '').toLowerCase();
  const properties = (Array.isArray(weapon?.properties) ? weapon.properties : [])
    .map((value) => String(value || '').toLowerCase());
  const propertyText = properties.join(' ');
  const dexterityMod = abilityModifier(abilities.dexterity);
  const strengthMod = abilityModifier(abilities.strength);
  const hasFinesse = propertyText.includes('finesse');
  const isRanged = (
    propertyText.includes('ammunition')
    || propertyText.includes('range')
    || /(bow|crossbow|sling|dart|blowgun|net)/i.test(name)
  );

  if (hasFinesse) {
    return dexterityMod > strengthMod ? 'dexterity' : 'strength';
  }

  return isRanged ? 'dexterity' : 'strength';
};

const inferWeaponRange = (weapon) => {
  if (weapon?.range) return weapon.range;
  if (weapon?.attackRange) return weapon.attackRange;

  const properties = Array.isArray(weapon?.properties) ? weapon.properties : [];
  for (const property of properties) {
    const match = String(property || '').match(RANGE_PROPERTY_PATTERN);
    if (match) {
      return `${match[1]}/${match[2]} ft.`;
    }
  }

  return null;
};

const rebuildWeaponDamage = (weapon, abilityKey, abilityMod) => {
  if (Array.isArray(weapon?.damage) && weapon.damage.length > 0) {
    return weapon.damage.map((entry, index) => (
      index === 0 && entry && typeof entry === 'object'
        ? {
            ...entry,
            dice: entry.dice || entry.formula || entry.damage || '',
            modifier: abilityMod
          }
        : deepClone(entry)
    ));
  }

  const damageText = String(weapon?.damage || weapon?.damageFormula || '').trim();
  const damageMatch = damageText.match(DAMAGE_DICE_PATTERN);
  const damageDice = damageMatch?.[1];

  if (!damageDice) {
    return damageText;
  }

  const modifierLabel = abilityMod >= 0 ? `+${abilityMod}` : String(abilityMod);
  return `${damageDice}${modifierLabel}`;
};

const rebuildWeaponEntries = (equipment, abilities, proficiencyBonus) => (
  equipment.weapons.map((weapon) => {
    const normalizedName = String(weapon?.name || '').toLowerCase();
    if (!normalizedName || normalizedName.includes('shield')) {
      return { ...weapon };
    }

    const abilityKey = inferWeaponAbility(weapon, abilities);
    const modifier = abilityModifier(abilities?.[abilityKey] ?? 10);
    const attackBonus = proficiencyBonus + modifier;

    return {
      ...weapon,
      attackBonus,
      damage: rebuildWeaponDamage(weapon, abilityKey, modifier),
      damageFormula: typeof weapon?.damageFormula === 'string'
        ? rebuildWeaponDamage({ damage: weapon.damageFormula }, abilityKey, modifier)
        : weapon?.damageFormula,
      range: inferWeaponRange(weapon) || weapon?.range || weapon?.attackRange || undefined
    };
  })
);

const deriveArmorClass = (equipment, abilities, classOption) => {
  const dexterityMod = abilityModifier(abilities.dexterity);
  const constitutionMod = abilityModifier(abilities.constitution);
  const wornArmor = equipment.armor.find((entry) => entry?.equipped !== false && !/shield/i.test(String(entry?.type || entry?.name || '')));

  let armorClass = 10 + dexterityMod;
  if (wornArmor) {
    const armorBase = Number(wornArmor?.armorClass);
    const armorType = String(wornArmor?.type || wornArmor?.name || '').toLowerCase();
    if (Number.isFinite(armorBase)) {
      if (armorType.includes('heavy')) {
        armorClass = armorBase;
      } else if (armorType.includes('medium')) {
        armorClass = armorBase + Math.min(dexterityMod, 2);
      } else if (armorType.includes('light')) {
        armorClass = armorBase + dexterityMod;
      } else {
        armorClass = armorBase + dexterityMod;
      }
    }
  } else if (classOption?.key === 'barbarian') {
    armorClass = 10 + dexterityMod + constitutionMod;
  }

  const shieldEntries = [
    ...equipment.armor.filter((entry) => /shield/i.test(String(entry?.type || entry?.name || ''))),
    ...equipment.weapons.filter((entry) => /shield/i.test(String(entry?.type || entry?.name || ''))),
    ...equipment.items.filter((entry) => /shield/i.test(String(entry?.type || entry?.name || '')))
  ];
  const shieldBonus = shieldEntries.reduce((total, entry) => {
    const numericBonus = Number(entry?.armorClass);
    if (Number.isFinite(numericBonus) && numericBonus > 0) {
      return total + numericBonus;
    }
    return total + SHIELD_BONUS;
  }, 0);

  return armorClass + shieldBonus;
};

const buildSpellcastingPayload = (baseTemplate, classOption, abilities, proficiencyBonus) => {
  const baseSpells = baseTemplate?.spells && typeof baseTemplate.spells === 'object'
    ? deepClone(baseTemplate.spells)
    : {};
  const baseSpellcasting = baseTemplate?.spellcasting && typeof baseTemplate.spellcasting === 'object'
    ? deepClone(baseTemplate.spellcasting)
    : null;
  const hasSpellPool = (
    Object.keys(baseSpells).length > 0
    || (baseSpellcasting && Object.keys(baseSpellcasting).length > 0)
  );

  if (!classOption?.spellcastingAbility || !hasSpellPool) {
    return {
      spells: baseSpells,
      spellcasting: baseSpellcasting
    };
  }

  const modifier = abilityModifier(abilities[classOption.spellcastingAbility]);
  const attackBonus = proficiencyBonus + modifier;
  const saveDC = 8 + attackBonus;

  return {
    spells: {
      ...baseSpells,
      ability: classOption.spellcastingAbility,
      attackBonus,
      saveDC
    },
    spellcasting: {
      ...(baseSpellcasting || {}),
      ability: classOption.spellcastingAbility,
      attackBonus,
      spellAttackBonus: attackBonus,
      saveDC,
      saveDc: saveDC,
      slots: baseSpells?.slots || baseSpellcasting?.slots || {}
    }
  };
};

const buildSavingThrowBonuses = (classOption, abilities, proficiencyBonus) => (
  ABILITY_ORDER.reduce((accumulator, ability) => {
    const proficient = (classOption?.savingThrows || []).includes(ability);
    const modifier = abilityModifier(abilities[ability]);
    accumulator[ability] = modifier + (proficient ? proficiencyBonus : 0);
    return accumulator;
  }, {})
);

const buildSkillPayload = (classOption, background, abilities, proficiencyBonus, rng) => {
  const classSkills = shuffle(
    rng,
    (classOption?.skillChoices || []).filter((skill) => !(background?.skills || []).includes(skill))
  ).slice(0, Number(classOption?.skillCount || 0));
  const selectedSkills = unique([...(background?.skills || []), ...classSkills]);
  const expertiseSkills = Number(classOption?.expertiseCount || 0) > 0
    ? shuffle(rng, selectedSkills).slice(0, classOption.expertiseCount)
    : [];

  const skillsDetailed = selectedSkills.map((name) => {
    const ability = SKILL_ABILITY_MAP[name] || 'wisdom';
    const modifier = abilityModifier(abilities[ability]);
    const expertise = expertiseSkills.includes(name);
    const bonus = modifier + proficiencyBonus + (expertise ? proficiencyBonus : 0);

    return {
      name,
      proficient: true,
      expertise,
      bonus
    };
  });

  const skills = skillsDetailed.reduce((accumulator, entry) => {
    accumulator[entry.name] = {
      proficient: entry.proficient,
      expertise: entry.expertise,
      value: entry.bonus
    };
    return accumulator;
  }, {});

  return {
    skills,
    skillsDetailed
  };
};

const buildFeaturePayload = (baseTemplate, species, background, ruleset) => {
  const classFeatures = (Array.isArray(baseTemplate?.features) ? baseTemplate.features : [])
    .map((entry) => normalizeFeatureEntry(entry))
    .filter(Boolean);
  const speciesTraits = (Array.isArray(species?.traits) ? species.traits : [])
    .map((entry) => normalizeFeatureEntry(entry))
    .filter(Boolean);
  const feats = ruleset === '2024' && background?.originFeat
    ? [
        normalizeFeatureEntry(
          {
            name: background.originFeat,
            description: `Origin feat granted by the ${background.label} background.`
          },
          `Origin feat granted by the ${background.label} background.`
        )
      ].filter(Boolean)
    : [];

  return {
    classFeatures,
    speciesTraits,
    feats
  };
};

const buildBiography = ({ classOption, species, background, name, personalityHook, alignment }) => ({
  description: `${name} is ${toArticle(species.label)} ${toSentenceCase(species.label)} ${toSentenceCase(classOption.label)} built for a fast start in VCS.`,
  backstory: background?.backstory || '',
  personality: personalityHook,
  ideals: `${alignment} tendencies guide how they approach danger and duty.`,
  bonds: `Feels responsible for the people and promises left behind by their ${toSentenceCase(background?.label) || 'past'}.`,
  flaws: 'Pushes forward before every detail is fully settled.'
});

const buildGeneratedId = ({ classKey, speciesKey, backgroundKey, seed }) => {
  if (seed !== undefined && seed !== null && seed !== '') {
    return `generated-${classKey}-${speciesKey}-${backgroundKey}-${stableHash(String(seed)).toString(36)}`;
  }

  return `generated-${classKey}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const generateStarterCharacter = (options = {}) => {
  const ruleset = resolveRuleset(options.ruleset);
  const tone = typeof options.tone === 'string' && options.tone.trim()
    ? options.tone.trim().toLowerCase()
    : DEFAULT_TONE;
  const level = clampLevel(options.level ?? DEFAULT_LEVEL);
  const rng = createRng(options.seed ?? `${ruleset}:${tone}:${level}:${STARTER_GENERATOR_VERSION}`);

  const classKey = resolveClassKey(options.classKey, rng);
  const backgroundKey = resolveBackgroundKey(options.backgroundKey, rng);
  const speciesKey = resolveSpeciesKey(options.speciesKey, ruleset, rng);

  const classOption = STARTER_CLASS_OPTIONS[classKey];
  const background = BACKGROUND_OPTIONS[backgroundKey];
  const species = SPECIES_OPTIONS_BY_RULESET[ruleset]?.[speciesKey];

  if (!classOption || !background || !species) {
    throw new Error('Unable to resolve starter character generation options.');
  }

  const baseTemplate = CharacterDataService.createCharacterFromTemplate(classOption.templateKey);
  const abilities = assignAbilityScores(classOption, background, species, ruleset, tone);
  const proficiencyBonus = proficiencyBonusForLevel(level);
  const initiative = abilityModifier(abilities.dexterity);
  const maxHp = Math.max(level, classOption.hitDie + abilityModifier(abilities.constitution));
  const name = typeof options.name === 'string' && options.name.trim()
    ? options.name.trim()
    : buildName(speciesKey, rng);
  const alignment = typeof options.alignment === 'string' && options.alignment.trim()
    ? options.alignment.trim()
    : pickOne(rng, ALIGNMENT_OPTIONS);
  const personalityHook = pickOne(rng, PERSONALITY_HOOKS) || '';
  const featurePayload = buildFeaturePayload(baseTemplate, species, background, ruleset);
  const { skills, skillsDetailed } = buildSkillPayload(classOption, background, abilities, proficiencyBonus, rng);
  const spellPayload = buildSpellcastingPayload(baseTemplate, classOption, abilities, proficiencyBonus);
  const proficiencies = {
    armor: unique(classOption.armorProficiencies || []),
    weapons: unique(classOption.weaponProficiencies || []),
    tools: unique([...(classOption.tools || []), ...(background.tools || [])]),
    savingThrows: unique(classOption.savingThrows || []),
    languages: unique([...(species.languages || []), ...(background.languages || [])])
  };
  const generatedId = buildGeneratedId({ classKey, speciesKey, backgroundKey, seed: options.seed });
  const equipment = normalizeEquipmentShape(baseTemplate?.equipment || {});
  const rebuiltEquipment = {
    ...equipment,
    weapons: rebuildWeaponEntries(equipment, abilities, proficiencyBonus)
  };
  const armorClass = deriveArmorClass(rebuiltEquipment, abilities, classOption);
  const biography = buildBiography({
    classOption,
    species,
    background,
    name,
    personalityHook,
    alignment
  });

  const mergedCharacter = buildSmartPasteCharacterImport(baseTemplate, {
    id: generatedId,
    name,
    class: classOption.label,
    classes: [
      {
        name: classOption.label,
        level,
        subclass: baseTemplate?.compat?.originalData?.classes?.[0]?.subclass || baseTemplate?.subclass || null,
        hitDice: `1d${classOption.hitDie}`
      }
    ],
    level,
    race: species.label,
    species: species.label,
    background: background.label,
    alignment,
    size: species.size,
    speed: species.speed,
    hp: maxHp,
    maxHp,
    proficiencyBonus,
    initiative,
    abilities,
    savingThrows: buildSavingThrowBonuses(classOption, abilities, proficiencyBonus),
    skills,
    skillsDetailed,
    proficiencies,
    toolProficiencies: proficiencies.tools,
    languages: proficiencies.languages,
    equipment: rebuiltEquipment,
    spells: spellPayload.spells,
    spellcasting: spellPayload.spellcasting,
    classFeatures: featurePayload.classFeatures,
    speciesTraits: featurePayload.speciesTraits,
    feats: featurePayload.feats,
    biography
  });

  const source = {
    name: 'Starter Character Generator',
    edition: ruleset === '2014' ? '5e 2014' : '5e 2024',
    license: 'Custom'
  };

  return hydrateCharacterCombatState({
    ...mergedCharacter,
    id: generatedId,
    type: 'character',
    name,
    class: classOption.label,
    level,
    classes: [
      {
        name: classOption.label,
        level,
        subclass: baseTemplate?.compat?.originalData?.classes?.[0]?.subclass || baseTemplate?.subclass || null,
        hitDice: `1d${classOption.hitDie}`
      }
    ],
    race: species.label,
    species: species.label,
    background: background.label,
    alignment,
    speed: species.speed,
    size: species.size,
    abilities,
    proficiencyBonus,
    hp: maxHp,
    maxHp,
    ac: armorClass,
    initiative,
    biography: {
      description: biography.description,
      backstory: biography.backstory,
      personalityTraits: personalityHook,
      ideals: biography.ideals,
      bonds: biography.bonds,
      flaws: biography.flaws
    },
    personalityTraits: personalityHook,
    ideals: biography.ideals,
    bonds: biography.bonds,
    flaws: biography.flaws,
    backstory: biography.backstory,
    notes: biography.description,
    equipment: rebuiltEquipment,
    spells: spellPayload.spells,
    spellcasting: spellPayload.spellcasting,
    classFeatures: featurePayload.classFeatures,
    speciesTraits: featurePayload.speciesTraits,
    feats: featurePayload.feats,
    features: featurePayload.classFeatures,
    skills,
    skillsDetailed,
    savingThrows: buildSavingThrowBonuses(classOption, abilities, proficiencyBonus),
    proficiencies,
    toolProficiencies: proficiencies.tools,
    languages: proficiencies.languages,
    source,
    _generationMeta: {
      generatorVersion: STARTER_GENERATOR_VERSION,
      mode: 'starter',
      seed: options.seed ?? null,
      tone,
      ruleset,
      classKey,
      speciesKey,
      backgroundKey
    }
  });
};

export default generateStarterCharacter;
